/** @jsxImportSource @emotion/react */

import { useEffect, useState } from "react";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import BaseButton from "components/common/Button/Base";
import { uploadPayrollToSemusarang } from "pages/remotes";
import { PALETTE } from "styles/base/variables";
import { runWithConcurrency } from "utils/concurrency";
import { PayeeType } from "models/payrollManagement.model";

// 업로드/카카오 발송과 동일하게, 동시 전송은 최대 SEND_BATCH_SIZE건으로 제한한다.
// 거래처 단위가 아니라 거래처×고용유형 조합 단위로 큐에 넣으므로, 한 거래처의 여러
// 유형도 서로 다른 워커에서 병렬로 전송될 수 있다.
const SEND_BATCH_SIZE = 4;

type SendStatus = "pending" | "sending" | "success" | "error";

interface SendItem {
  id: string; // `${businessId}:${payeeType}` (전송 대상 없으면 businessId 그대로)
  businessId: string;
  name: string; // "거래처명(유형)" — 전송 대상 없으면 "거래처명(해당없음)"
  payeeType: PayeeType | null;
  yearMonth: string;
  status: SendStatus;
  errorMessage?: string;
}

export interface PayrollSendTarget {
  id: string; // businessId
  name: string;
  yearMonth: string; // yyyy-MM (귀속연월, grid.logic.accrualPeriod 결과)
  payeeTypes: { payeeType: PayeeType; label: string }[];
}

interface PayrollSendDialogProps {
  isOpen: boolean;
  businesses: PayrollSendTarget[];
  onClose: () => void;
  onFinished: () => void;
}

function PayrollSendDialog({
  isOpen,
  businesses,
  onClose,
  onFinished
}: PayrollSendDialogProps) {
  const [items, setItems] = useState<SendItem[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    // 거래처×고용유형 조합 하나가 항목 하나. 마감 완료된 유형이 없는 거래처는
    // 보낼 게 없다는 뜻이므로 "해당없음" 항목 하나로 표시하고 바로 완료 처리한다.
    const initial: SendItem[] = businesses.flatMap((business): SendItem[] =>
      business.payeeTypes.length
        ? business.payeeTypes.map((pt) => ({
            id: `${business.id}:${pt.payeeType}`,
            businessId: business.id,
            name: `${business.name}(${pt.label})`,
            payeeType: pt.payeeType,
            yearMonth: business.yearMonth,
            status: "pending"
          }))
        : [
            {
              id: business.id,
              businessId: business.id,
              name: `${business.name}(해당없음)`,
              payeeType: null,
              yearMonth: business.yearMonth,
              status: "pending"
            }
          ]
    );
    setItems(initial);
    setIsSending(true);
    setCompletedCount(0);

    let cancelled = false;

    const sendOne = async (item: SendItem) => {
      setItems((prev) =>
        prev.map((it) =>
          it.id === item.id ? { ...it, status: "sending" } : it
        )
      );

      if (!item.payeeType) {
        // 마감 완료된 급여자료가 없어 전송할 대상이 없음 — 실패가 아니라 완료로 처리한다.
        if (!cancelled)
          setItems((prev) =>
            prev.map((it) =>
              it.id === item.id ? { ...it, status: "success" } : it
            )
          );
        if (!cancelled) setCompletedCount((prev) => prev + 1);
        return;
      }

      try {
        await uploadPayrollToSemusarang({
          businessId: item.businessId,
          yearMonth: item.yearMonth,
          payeeType: item.payeeType
        });
        if (cancelled) return;
        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id ? { ...it, status: "success" } : it
          )
        );
      } catch (err: any) {
        if (cancelled) return;
        const errorMessage =
          err.response?.data?.message || "전송에 실패했습니다.";
        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id ? { ...it, status: "error", errorMessage } : it
          )
        );
      } finally {
        if (!cancelled) setCompletedCount((prev) => prev + 1);
      }
    };

    runWithConcurrency(initial, SEND_BATCH_SIZE, sendOne, () => cancelled).then(
      () => {
        if (cancelled) return;
        setIsSending(false);
        onFinished();
      }
    );

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const totalCount = items.length;
  const progressPercent =
    totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const successCount = items.filter((it) => it.status === "success").length;
  const errorCount = items.filter((it) => it.status === "error").length;

  const renderStatusIcon = (status: SendStatus) => {
    switch (status) {
      case "success":
        return (
          <CheckCircleIcon
            fontSize="small"
            css={css`
              color: ${PALETTE.progressCompleted};
            `}
          />
        );
      case "error":
        return (
          <ErrorIcon
            fontSize="small"
            css={css`
              color: ${PALETTE.error};
            `}
          />
        );
      case "sending":
        return (
          <span
            css={css`
              font-size: 12px;
              color: ${PALETTE.primary};
            `}
          >
            전송 중
          </span>
        );
      default:
        return (
          <span
            css={css`
              font-size: 12px;
              color: ${PALETTE.textMid};
            `}
          >
            대기
          </span>
        );
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        if (!isSending) onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>급여 세무사랑 전송 ({totalCount}건)</DialogTitle>
      <DialogContent>
        <div
          css={css`
            min-width: 400px;
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          {!isSending && (
            <div
              css={css`
                font-size: 13px;
                color: ${PALETTE.textMid};
              `}
            >
              전송 완료 {successCount}건
              {errorCount > 0 && ` · 전송 실패 ${errorCount}건`}
            </div>
          )}

          <div
            css={css`
              max-height: 320px;
              overflow-y: auto;
              border: 1px solid ${PALETTE.border};
              border-radius: 8px;
            `}
          >
            {items.map((item) => (
              <div
                key={item.id}
                css={css`
                  padding: 10px 14px;
                  display: flex;
                  align-items: flex-start;
                  gap: 10px;
                  border-bottom: 1px solid ${PALETTE.borderLight};

                  &:last-child {
                    border-bottom: none;
                  }
                `}
              >
                <div
                  css={css`
                    flex: 1;
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                  `}
                >
                  <div
                    css={css`
                      font-size: 13px;
                      color: ${PALETTE.text};
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                    `}
                  >
                    {item.name}
                  </div>
                  {item.status === "error" && item.errorMessage && (
                    <div
                      css={css`
                        font-size: 12px;
                        color: ${PALETTE.error};
                      `}
                    >
                      {item.errorMessage}
                    </div>
                  )}
                </div>
                {renderStatusIcon(item.status)}
              </div>
            ))}
          </div>

          {isSending && (
            <div>
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{
                  backgroundColor: PALETTE.progressBackground,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: PALETTE.progressCompleted
                  }
                }}
              />
              <div
                css={css`
                  margin-top: 4px;
                  text-align: right;
                  font-size: 12px;
                  color: ${PALETTE.textMid};
                `}
              >
                {completedCount}/{totalCount}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <div
        css={css`
          margin: 6px 24px;
          border-top: 1px solid ${PALETTE.border};
        `}
      />
      <DialogActions
        css={css`
          padding: 12px 24px 20px 24px;
        `}
      >
        <BaseButton
          variant="contained"
          color={PALETTE.primary}
          disabled={isSending}
          onClick={onClose}
        >
          닫기
        </BaseButton>
      </DialogActions>
    </Dialog>
  );
}

export default PayrollSendDialog;
